import { FC } from 'react'

import { useFragment } from '@apollo/client'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { F0AvatarList } from '@factorialco/f0-react'
import { Box, Text } from '@factorialco/deprecated-design-system'
import DOMPurify from 'dompurify'
import { TrainingsTrainingRequestApprovableFragmentDoc } from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { EmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'
import NoteField from 'modules/inbox_tasks/components/NoteField'

type TrainingsTrainingRequestTaskContentProps = {
  id: string
  requesterContent: EmployeeTableViewRow
}

const TrainingsTrainingRequestTaskContent: FC<TrainingsTrainingRequestTaskContentProps> = ({
  id,
  requesterContent,
}) => {
  const { data } = useFragment({
    fragment: TrainingsTrainingRequestApprovableFragmentDoc,
    fragmentName: 'TrainingsTrainingRequestApprovable',
    from: {
      __typename: 'TrainingsTrainingRequest',
      id,
    },
    optimistic: false,
  })

  if (!data) {
    return null
  }

  const participantAvatars = (data.participants ?? []).flatMap((p) => {
    if (!p) return []
    return [
      {
        firstName: p.firstName ?? '',
        lastName: p.lastName ?? '',
        src: p.avatar?.url ? buildUrl(p.avatar.url) : undefined,
        type: 'person' as const,
      },
    ]
  })

  const details = [
    requesterContent,
    data.trainingName && {
      title: i18n.t('tasks.approvals.trainings_training_request.fields.training_name'),
      content: {
        text: data.trainingName,
        type: 'item' as const,
      },
    },
  ].filter(Boolean)

  const trainingNeedText = data?.trainingNeed
    ? DOMPurify.sanitize(data?.trainingNeed, { ALLOWED_TAGS: [] })
    : ''
  const trainingCommentText = data?.comments
    ? DOMPurify.sanitize(data?.comments, { ALLOWED_TAGS: [] })
    : ''

  return (
    <>
      <DetailsItemsList details={details} tableView />
      {!!trainingNeedText && (
        <NoteField
          label={i18n.t('tasks.approvals.trainings_training_request.fields.training_need')}
          content={trainingNeedText}
        />
      )}
      {participantAvatars.length > 0 && (
        <Box flexDirection="column" gap="s8">
          <Text color="textSecondary" size="200">
            {i18n.t('tasks.approvals.trainings_training_request.fields.participants')}
          </Text>
          <F0AvatarList avatars={participantAvatars} size="md" type="person" max={5} />
        </Box>
      )}
      {!!trainingCommentText && (
        <NoteField
          label={i18n.t('tasks.approvals.trainings_training_request.fields.additional_info')}
          content={trainingCommentText}
        />
      )}
    </>
  )
}

export default TrainingsTrainingRequestTaskContent

export * from './metadata'
